import { useRef, useMemo, useEffect } from "react";

// ✅ Helper: Create a silent black video track (keeps connection alive)
const createBlackTrack = () => {
  const canvas = document.createElement("canvas");
  canvas.width = 640;
  canvas.height = 480;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "black";
  ctx.fillRect(0, 0, 640, 480);
  ctx.fillStyle = "white";
  ctx.font = "40px Arial";
  ctx.textAlign = "center";
  ctx.fillText("VIDEO PAUSED", 320, 240);
  const stream = canvas.captureStream(10); // Low FPS to save bandwidth
  return stream.getVideoTracks()[0];
};

export const useBroadcasterPeer = ({ pcRef, onIceCandidate }) => {
  const videoSenderRef = useRef(null);
  const localStreamRef = useRef(null); 
  const callbacksRef = useRef({ onIceCandidate });
  
  useEffect(() => { callbacksRef.current = { onIceCandidate }; }, [onIceCandidate]);

  const createPC = (iceConfig) => {
    if (pcRef.current) return pcRef.current;
    
    console.log("🛠️ Creating Broadcaster PeerConnection");
    const pc = new RTCPeerConnection(iceConfig);
    
    pc.onicecandidate = (e) => {
        if(e.candidate && callbacksRef.current.onIceCandidate) {
            callbacksRef.current.onIceCandidate(e.candidate);
        }
    };
    
    pcRef.current = pc;
    return pc;
  };

  const addLocalTracksOnce = async (stream) => {
    const pc = pcRef.current;
    if (!pc) return;
    
    localStreamRef.current = stream; // Store reference for later cleanup
    console.log("🎥 [Peer] Adding Initial Tracks...");
    
    // Cleanup old senders
    pc.getSenders().forEach(s => {
        try { pc.removeTrack(s); } catch(e){}
    });

    stream.getTracks().forEach(track => {
        if (track.kind === 'video') {
            const sender = pc.addTrack(track, stream);
            videoSenderRef.current = sender; // ✅ Capture Video Sender
        } else {
            pc.addTrack(track, stream); // Audio just gets added
        }
    });
  };

  // ✅ FIXED TOGGLE LOGIC
  const toggleVideo = async (enabled) => {
    const sender = videoSenderRef.current;
    if (!sender) {
        console.error("❌ Cannot toggle: No Video Sender found.");
        return null;
    }

    try {
        if (!enabled) {
            console.log("🚫 Muting Video: Switching to Black Track...");
            
            // 1. Network: Send Black Frames to Viewer
            const blackTrack = createBlackTrack();
            await sender.replaceTrack(blackTrack);

            // 2. Hardware: KILL Camera Light
            if (localStreamRef.current) {
                const videoTracks = localStreamRef.current.getVideoTracks();
                videoTracks.forEach(t => {
                    console.log("🛑 Stopping Hardware Track:", t.label);
                    t.stop();     // Turns off light
                    t.enabled = false;
                });
            }
            return "stopped";

        } else {
            console.log("📸 Unmuting: Restarting Camera...");
            
            // 1. Hardware: Request ONLY Video (Audio is already running!)
            const newStream = await navigator.mediaDevices.getUserMedia({ video: true });
            const newVideoTrack = newStream.getVideoTracks()[0];
            
            // 2. Network: Send Real Video to Viewer
            await sender.replaceTrack(newVideoTrack);
            
            // 3. Update Local Ref so we can stop it next time
            if (localStreamRef.current) {
                // Remove old dead tracks
                localStreamRef.current.getVideoTracks().forEach(t => localStreamRef.current.removeTrack(t));
                // Add new live track
                localStreamRef.current.addTrack(newVideoTrack);
            }

            return newVideoTrack; // Return track to update UI
        }
    } catch(e) { 
        console.error("Toggle Video Error:", e); 
        return null;
    }
  };

  const close = () => {
    if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(t => t.stop());
    }
    if (pcRef.current) {
        pcRef.current.close();
        pcRef.current = null;
    }
    videoSenderRef.current = null;
  };

  return useMemo(() => ({ 
      videoSenderRef, createPC, addLocalTracksOnce, toggleVideo, close
  }), []);
};